import { useQuery } from "@apollo/client/react";
import { useRef } from "react";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { Link, useNavigate } from "react-router";
import { useMe } from "@/auth/use-me";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { Avatar } from "@/components/common/Avatar";
import { WelcomeHint } from "@/components/home/WelcomeHint";
import {
  HeroRevealImage,
  QUICK_ACTION_CLASS,
  QuickActionLink,
  SplitStatTile,
  SplitTileShell,
  StatTile,
} from "@/components/dashboard/StatTile";
import { visitorName } from "@/lib/profile-visitor";
import { NoBackend } from "@/components/common/NoBackend";
import { ShareItemCardCover } from "@/components/share/ShareItemCard";
import { HorizontalCardCarousel } from "@/components/design/HorizontalCardCarousel";
import {
  HomeGrowInsightsDocument,
  HomeSaveInsightsDocument,
  MatchListDocument,
  MatchMode as GqlMatchMode,
  MyselfDocument,
  MySharedItemsDocument,
} from "@/graphql/generated/operations";
import { useSavedResultCount } from "@/components/match/use-saved-result-count";
import { ELEMENT_CODES } from "@/lib/elements";
import { formatDate } from "@/lib/format-date";
import { rankLabel } from "@/lib/grow-rank";
import { setPendingUpload } from "@/lib/pending-upload";
import { POWER_BY_ABBR } from "@/lib/power-library";
import { creatorName } from "@/lib/share-item";

// HomePage - node-by-node port of workshop's pages/HomePage.tsx. Light
// greeting (random "Hi" / "Hallo" + first name) and four dashboard
// strips (Save · Grow · Match · Share). Save + Grow are NoBackend
// placeholders for now; Match shows the user's draft matches and
// Share shows their published items, both as horizontally-scrolling
// rows.

const HELLOS = ["Hi", "Hallo"] as const;
const HELLO = HELLOS[Math.floor(Math.random() * HELLOS.length)];

interface MatchNode {
  id: string;
  name?: string | null;
  description?: string | null;
  candidateCount?: number | null;
  matchMode?: GqlMatchMode | null;
  updatedAt?: string | null;
  createdBy?: {
    formattedName?: string | null;
    name?: { given?: string[] | null; family?: string | null };
  } | null;
}

// "+ Datei hochladen" quick action for the Service tile. The file dialog opens
// here on the click (a valid user gesture); the chosen files are handed to
// ServicePage's own upload flow via setPendingUpload, then we navigate there.
function ServiceUploadAction() {
  const navigate = useNavigate();
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={QUICK_ACTION_CLASS}
      >
        <span className="text-ink/40">+</span>
        {intl.formatMessage({
          id: "home.uploadFile",
          defaultMessage: "Upload file",
        })}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const list = e.target.files;
          if (!list || !list.length) return;
          const files = Array.from(list);
          e.target.value = "";
          setPendingUpload(files);
          navigate("/save/documents");
        }}
      />
    </>
  );
}

// element enum → { abbr, name } via the shared power library (same mapping the
// Grow pages use), so an insight tile can show the localized power name + link.
function powerName(
  element: string,
  intl: IntlShape,
): { abbr: string; name: string } | null {
  const abbr = ELEMENT_CODES[element];
  if (!abbr) return null;
  const p = POWER_BY_ABBR[abbr];
  return p ? { abbr, name: intl.formatMessage({ id: p.nameId }) } : null;
}

// Compact strengths/room-to-grow rows for the Grow "split" tile — name + score, linking straight
// to the power detail page. No bars: three of these per column
// in a tile this size, a bar per row would be more chrome than signal.
function PowerMiniList({
  rows,
  intl,
}: {
  rows: Array<{ element: string; result?: number | null }>;
  intl: IntlShape;
}) {
  if (rows.length === 0) {
    return (
      <div className="mt-1 text-[11px] text-ink/55">
        {intl.formatMessage({
          id: "home.notRatedYet",
          defaultMessage: "not rated yet",
        })}
      </div>
    );
  }
  return (
    <ul className="mt-1 space-y-0.5">
      {rows.map((r) => {
        const p = powerName(String(r.element), intl);
        if (!p) return null;
        return (
          <li key={String(r.element)}>
            <Link
              to={`/grow/power/${p.abbr}`}
              // text-sm, not text-[12px]: UI_CONVENTIONS' field type scale calls this exact
              // shape ("list-row titles") .field-value = 14px, one step above the meta scale
              // this used before.
              className="-mx-1.5 flex items-center justify-between gap-2 rounded-md px-1.5 py-1 text-sm transition hover:bg-ink/[0.06] hover:text-accent"
            >
              <span className="min-w-0 truncate">{p.name}</span>
              <span className="shrink-0 tabular-nums text-ink/45">
                {r.result}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function Dashboard({
  title,
  to,
  right,
  graphql,
  anchor,
  children,
}: {
  title: string;
  to: string;
  right?: string;
  graphql?: string;
  /** data-assistant-anchor for the main-page tour (one stop per dashboard). */
  anchor?: string;
  children: ReactNode;
}) {
  const intl = useIntl();
  return (
    <section
      className="mt-10 first:mt-0"
      data-graphql={graphql}
      data-assistant-anchor={anchor}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <Link
          to={to}
          className="font-serif text-2xl leading-none transition hover:text-accent"
        >
          {title}
        </Link>
        <Link
          to={to}
          className="text-[10px] uppercase tracking-[0.25em] text-ink/45 transition hover:text-ink"
        >
          {right ??
            intl.formatMessage({
              id: "home.viewAll",
              defaultMessage: "View all ↗",
            })}
        </Link>
      </div>
      {children}
    </section>
  );
}

// Ported from MatchPage's own history card (pages/match/MatchPage.tsx) so a match looks the same
// wherever it shows up — mode label, name, creator, date · candidates · results footer. Two things
// only Home needs: the description and the "jump to result" link start collapsed and only expand
// on hover (MatchPage's card always shows the description; Home's grid is denser, four cards next
// to Save/Grow's own four). Own component, not inlined in the .map(): useSavedResultCount is a
// hook, so it needs one call per card, not one call per row.
function HomeMatchCard({
  match: m,
  intl,
}: {
  match: MatchNode;
  intl: IntlShape;
}) {
  const resultsCount = useSavedResultCount(m.id);
  return (
    <div className="box group relative flex min-h-32 flex-col overflow-hidden bg-surface p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:ring-ink/15">
      <HeroRevealImage src="/heroes/match.jpg" />
      {/* relative: same stacking fix as StatTile — an absolutely positioned reveal sibling would
          otherwise paint above this static text regardless of DOM order. */}
      <div className="relative flex flex-1 flex-col">
        <Link to={`/match/${m.id}`} className="flex flex-col">
          {/* field-label, not an ad-hoc text-[10px]: UI_CONVENTIONS' field type scale. */}
          <div className="field-label">
            <FormattedMessage
              id={`match.workflow.mode${
                m.matchMode === GqlMatchMode.Assess
                  ? "Assess"
                  : m.matchMode === GqlMatchMode.Rank
                    ? "Rank"
                    : "Optimize"
              }`}
              defaultMessage="Optimize"
            />
          </div>
          {/* line-clamp-1, not -2 (MatchPage's card allows 3 for its description, but not for the
              name either): items-start on the surrounding grid means a 2-line name would make just
              this one card taller than its row siblings. */}
          <div className="mt-0.5 text-sm font-medium leading-tight line-clamp-1">
            {m.name ||
              intl.formatMessage({
                id: "home.unnamedMatch",
                defaultMessage: "Unnamed match",
              })}
          </div>
          <div className="mt-1 line-clamp-1 text-[11px] text-ink/55">
            {creatorName(m.createdBy) || m.createdBy?.formattedName || "–"}
          </div>
          <div className="mt-4 text-[10px] text-ink/45">
            {[
              formatDate(m.updatedAt, intl.locale) || null,
              intl.formatMessage(
                {
                  id: "home.candidatesCount",
                  defaultMessage:
                    "{count, plural, one {# candidate} other {# candidates}}",
                },
                { count: m.candidateCount ?? 0 },
              ),
              resultsCount
                ? intl.formatMessage(
                    {
                      id: "match.landing.resultsCount",
                      defaultMessage: "{count} results",
                    },
                    { count: resultsCount },
                  )
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </Link>
        {m.description || resultsCount > 0 ? (
          // Same expand-on-hover mechanic as StatTile's own `actions` (grid-rows 0fr→1fr).
          <div className="grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity,margin-top] duration-500 ease-out group-hover:mt-3 group-hover:grid-rows-[1fr] group-hover:opacity-100">
            <div className="flex min-h-0 flex-col gap-1 overflow-hidden">
              {m.description ? (
                <p className="text-[11px] leading-snug text-ink/70 line-clamp-3">
                  {m.description}
                </p>
              ) : null}
              {resultsCount > 0 ? (
                <QuickActionLink to={`/match/${m.id}?step=result`} icon="→">
                  {intl.formatMessage({
                    id: "home.viewResult",
                    defaultMessage: "View result",
                  })}
                </QuickActionLink>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function HomePage() {
  // 242-access-model: the Grow/Match dashboard cards and their queries only
  // run when the respective feature is in the whitelist, same gate as the
  // sidebar pillars and the routes themselves (@/router.tsx), just applied
  // to a card instead of a page.
  const hasGrow = useHasFeature(FEATURE_CATALOG.grow.key);
  const hasMatch = useHasFeature(FEATURE_CATALOG.match.key);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const matchInput: any = { first: 20 };
  const {
    data: matchData,
    loading: matchLoading,
    error: matchError,
  } = useQuery(MatchListDocument, {
    variables: { input: matchInput },
    skip: !hasMatch,
  });
  const matches = (matchData?.matches?.nodes ?? []) as MatchNode[];
  // Home only needs a pulse, not the full list: the 4 matches you're actively working on right
  // now, most-recently-updated first — not every match you have access to.
  const recentMatches = [...matches]
    .sort(
      (a, b) =>
        (Date.parse(b.updatedAt ?? "") || 0) -
        (Date.parse(a.updatedAt ?? "") || 0),
    )
    .slice(0, 4);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const myInput: any = { first: 20, where: { owner: { eq: true } } };
  const {
    data: myData,
    loading: myLoading,
    error: myError,
  } = useQuery(MySharedItemsDocument, {
    variables: { input: myInput },
  });
  const myItems = myData?.sharedItems?.nodes ?? [];

  const me = useMe();
  const intl = useIntl();

  // Save + Grow quick-jump insights (lean dashboard queries). Myself is reused
  // for the Profil entry count (deduped with useMe's own Myself, cache-first).
  const saveQ = useQuery(HomeSaveInsightsDocument);
  const growQ = useQuery(HomeGrowInsightsDocument, { skip: !hasGrow });
  const { data: myselfData } = useQuery(MyselfDocument);

  const saveLoading = saveQ.loading && !saveQ.data;
  // "Service" mirrors ServicePage: documents + curriculum vitaes combined.
  const docCount =
    (saveQ.data?.documents.totalCount ?? 0) +
    (saveQ.data?.curriculumVitaes.totalCount ?? 0);
  const contactCount = saveQ.data?.myContactCount ?? 0;
  const communityCount = saveQ.data?.personGroups.personGroups.length ?? 0;
  const pins = saveQ.data?.numberOfPinnedItems;
  const pinTotal =
    (pins?.pinnedPersons ?? 0) +
    (pins?.pinnedProjects ?? 0) +
    (pins?.pinnedPosts ?? 0);
  const visitors = saveQ.data?.recentProfileVisitors ?? [];

  // Profil KPI: total real CV entries (consistent with the other count tiles).
  const m = myselfData?.myself;
  const profileEntryCount =
    (m?.experiences?.length ?? 0) +
    (m?.educations?.length ?? 0) +
    (m?.expertises?.length ?? 0) +
    (m?.certifications?.length ?? 0) +
    (m?.languageSkills?.length ?? 0);

  const growLoading = growQ.loading && !growQ.data;
  const ranking = growQ.data?.growRanking;
  const growScore = ranking?.score ?? 0;

  const selfResults = growQ.data?.selfAssessmentResults ?? [];

  // "Als Nächstes": open self-assessments, closest to completion first.
  const upNext = [...selfResults]
    .filter(
      (r) => r.questionCount > 0 && r.answeredQuestionCount < r.questionCount,
    )
    .sort((a, b) => b.answeredQuestionCount - a.answeredQuestionCount)
    .slice(0, 4);
  const firstUpNext = upNext[0]
    ? powerName(String(upNext[0].element), intl)
    : null;

  // Strengths / room to grow: only powers with a numeric self-assessment score.
  const scoredPowers = [...selfResults]
    .filter((r) => typeof r.result === "number")
    .sort((a, b) => (b.result ?? 0) - (a.result ?? 0));
  const strengths = scoredPowers.slice(0, 3);
  const weaknesses = scoredPowers.slice(-3).reverse();

  const topContacts = growQ.data?.contactsWithHighestScore ?? [];

  // Guided tour (/tour, and the "main page" section of /tour-all): the four dashboards.
  useAssistantTour({
    label: intl.formatMessage({
      id: "home.tour.label",
      defaultMessage: "Home",
    }),
    summary: intl.formatMessage({
      id: "home.tour.summary",
      defaultMessage:
        "This is the main page: the latest numbers from every section at a glance. Every tile links to its spot in the app.",
    }),
    // Grow/Match stops are omitted entirely when the feature isn't reachable
    // (empty-array spread): the anchor those stops point at is never
    // rendered below, so keeping the stop would point the tour at nothing.
    stops: [
      {
        anchor: "home.save",
        title: intl.formatMessage({
          id: "home.tour.save.title",
          defaultMessage: "Save",
        }),
        description: intl.formatMessage({
          id: "home.tour.save.desc",
          defaultMessage:
            "Your profile entries, documents, contacts and pins, with quick actions to add new ones.",
        }),
      },
      ...(hasGrow
        ? [
            {
              anchor: "home.grow",
              title: intl.formatMessage({
                id: "home.tour.grow.title",
                defaultMessage: "Grow",
              }),
              description: intl.formatMessage({
                id: "home.tour.grow.desc",
                defaultMessage:
                  "Your profile progress, strongest powers and open self-assessments.",
              }),
            },
          ]
        : []),
      {
        anchor: "home.share",
        title: intl.formatMessage({
          id: "home.tour.share.title",
          defaultMessage: "Share",
        }),
        description: intl.formatMessage({
          id: "home.tour.share.desc",
          defaultMessage:
            "Your posts and projects and what is happening around them.",
        }),
      },
      ...(hasMatch
        ? [
            {
              anchor: "home.match",
              title: intl.formatMessage({
                id: "home.tour.match.title",
                defaultMessage: "Match",
              }),
              description: intl.formatMessage({
                id: "home.tour.match.desc",
                defaultMessage:
                  "Your matches. Click one to continue where you left off.",
              }),
            },
          ]
        : []),
    ],
  });

  return (
    <section className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <WelcomeHint />
      <header className="mb-8" data-graphql="Myself, MyAvatar">
        <h1 className="font-serif text-4xl leading-tight tracking-tight @sm:text-5xl">
          {HELLO}
          {me.firstName ? `, ${me.firstName}` : ""}
        </h1>
        <p className="mt-2 text-sm text-ink/55">
          {intl.formatMessage({
            id: "home.greeting",
            defaultMessage: "Glad you're here.",
          })}
        </p>
      </header>

      <Dashboard
        title="Save"
        to="/save/basics"
        graphql="HomeSaveInsights"
        anchor="home.save"
      >
        {" "}
        {/* i18n-allow: SAVE brand name */}
        <div className="grid grid-cols-2 items-start gap-3 @sm:grid-cols-4">
          <StatTile
            heroImageSrc="/heroes/save.jpg"
            to="/save/basics"
            label={intl.formatMessage({
              id: "home.profile",
              defaultMessage: "Profile",
            })}
            value={profileEntryCount}
            sub={intl.formatMessage(
              profileEntryCount
                ? { id: "home.entries", defaultMessage: "entries" }
                : {
                    id: "home.profileEmpty",
                    defaultMessage: "nothing recorded yet",
                  },
            )}
            loading={!myselfData}
            actions={
              <>
                <QuickActionLink to="/save/basics?add=experience">
                  {intl.formatMessage({
                    id: "home.workExperience",
                    defaultMessage: "Work experience",
                  })}
                </QuickActionLink>
                <QuickActionLink to="/save/basics?add=education">
                  {intl.formatMessage({
                    id: "home.education",
                    defaultMessage: "Education",
                  })}
                </QuickActionLink>
                <QuickActionLink to="/save/basics?add=certificate">
                  {intl.formatMessage({
                    id: "home.certificate",
                    defaultMessage: "Certificate",
                  })}
                </QuickActionLink>
              </>
            }
          />
          <StatTile
            heroImageSrc="/heroes/save.jpg"
            to="/save/documents"
            label={intl.formatMessage({
              id: "home.service",
              defaultMessage: "Documents",
            })}
            value={docCount}
            sub={intl.formatMessage(
              docCount
                ? { id: "home.documents", defaultMessage: "Documents" }
                : {
                    id: "home.serviceEmpty",
                    defaultMessage: "nothing filed yet",
                  },
            )}
            loading={saveLoading}
            actions={<ServiceUploadAction />}
          />
          <SplitStatTile
            heroImageSrc="/heroes/save.jpg"
            left={{
              to: "/save/contact",
              label: intl.formatMessage({
                id: "home.community",
                defaultMessage: "Community",
              }),
              value: contactCount,
              sub: communityCount
                ? intl.formatMessage(
                    {
                      id: "home.communitiesSub",
                      defaultMessage:
                        "{count, plural, one {# community} other {# communities}}",
                    },
                    { count: communityCount },
                  )
                : intl.formatMessage(
                    contactCount
                      ? { id: "home.contacts", defaultMessage: "Contacts" }
                      : {
                          id: "home.noContacts",
                          defaultMessage: "no contacts yet",
                        },
                  ),
              linkLabel: intl.formatMessage({
                id: "home.viewCommunities",
                defaultMessage: "Show communities",
              }),
            }}
            right={{
              to: "/save/pinboard",
              label: intl.formatMessage({
                id: "home.pins",
                defaultMessage: "Pins",
              }),
              value: pinTotal,
              sub: pinTotal ? (
                <div className="space-y-0.5">
                  <div>
                    {intl.formatMessage(
                      {
                        id: "home.personsCount",
                        defaultMessage:
                          "{count, plural, one {# person} other {# people}}",
                      },
                      { count: pins?.pinnedPersons ?? 0 },
                    )}
                  </div>
                  <div>
                    {intl.formatMessage(
                      {
                        id: "home.projectsCount",
                        defaultMessage:
                          "{count, plural, one {# project} other {# projects}}",
                      },
                      { count: pins?.pinnedProjects ?? 0 },
                    )}
                  </div>
                  <div>
                    {intl.formatMessage(
                      {
                        id: "home.postsCount",
                        defaultMessage:
                          "{count, plural, one {# post} other {# posts}}",
                      },
                      { count: pins?.pinnedPosts ?? 0 },
                    )}
                  </div>
                </div>
              ) : (
                intl.formatMessage({
                  id: "home.noPins",
                  defaultMessage: "nothing pinned yet",
                })
              ),
              linkLabel: intl.formatMessage({
                id: "home.viewPins",
                defaultMessage: "Show pins",
              }),
            }}
          />
          <StatTile
            heroImageSrc="/heroes/save.jpg"
            to={
              visitors[0]?.person?.id ? `/p/${visitors[0].person.id}` : "/save/basics"
            }
            label={intl.formatMessage({
              id: "home.profileVisitors",
              defaultMessage: "Profile visitors",
            })}
            value={visitors.length}
            sub={
              visitors.length
                ? intl.formatMessage(
                    { id: "home.lastVisitor", defaultMessage: "last: {name}" },
                    { name: visitorName(visitors[0]) },
                  )
                : intl.formatMessage({
                    id: "home.noVisitsYet",
                    defaultMessage: "no views yet",
                  })
            }
            loading={saveLoading}
            actions={
              visitors.length ? (
                <>
                  {visitors.slice(0, 4).map((v) => (
                    <QuickActionLink
                      key={v.person.id}
                      to={`/p/${v.person.id}`}
                      icon={
                        <Avatar
                          size="sm"
                          person={{
                            name: visitorName(v),
                            avatarContent: v.person.avatar?.content,
                            avatarMime: v.person.avatar?.mimeType,
                          }}
                        />
                      }
                    >
                      {visitorName(v)}
                    </QuickActionLink>
                  ))}
                </>
              ) : undefined
            }
          />
        </div>
      </Dashboard>

      {hasGrow ? (
        <Dashboard
          title="Grow"
          to="/grow/map"
          graphql="HomeGrowInsights"
          anchor="home.grow"
        >
          {" "}
          {/* i18n-allow: GROW brand name */}
          <div className="grid grid-cols-2 items-start gap-3 @sm:grid-cols-4">
            <StatTile
              heroImageSrc="/heroes/grow.png"
              to="/grow/map"
              label={intl.formatMessage({
                id: "home.profileProgress",
                defaultMessage: "Profile progress",
              })}
              value={`${growScore} %`}
              bar={growScore}
              sub={
                ranking
                  ? rankLabel(ranking.userRank, intl.locale)
                  : intl.formatMessage({
                      id: "home.notStarted",
                      defaultMessage: "not started yet",
                    })
              }
              loading={growLoading}
              actions={
                <>
                  <p className="px-2 text-[11px] leading-snug text-ink/45">
                    <FormattedMessage
                      id="grow.landing.progressHint"
                      defaultMessage="Answer all questions about the powers to increase your profile progress."
                    />
                  </p>
                  <QuickActionLink to="/grow/map" icon="→">
                    {intl.formatMessage({
                      id: "home.viewPowerMap",
                      defaultMessage: "Show Power Map",
                    })}
                  </QuickActionLink>
                </>
              }
            />
            <StatTile
              heroImageSrc="/heroes/grow.png"
              to="/grow/map"
              label={intl.formatMessage({
                id: "home.upNext",
                defaultMessage: "Up next",
              })}
              value={upNext.length}
              sub={
                firstUpNext
                  ? intl.formatMessage(
                      {
                        id: "home.upNextFirst",
                        defaultMessage: "next: {name}",
                      },
                      { name: firstUpNext.name },
                    )
                  : intl.formatMessage({
                      id: "home.allAssessmentsDone",
                      defaultMessage: "all self-assessments completed",
                    })
              }
              loading={growLoading}
              actions={
                upNext.length ? (
                  <>
                    {upNext.map((r) => {
                      const p = powerName(String(r.element), intl);
                      if (!p) return null;
                      return (
                        <QuickActionLink
                          key={String(r.element)}
                          to={`/grow/power/${p.abbr}`}
                          icon="→"
                        >
                          {p.name} · {r.answeredQuestionCount}/{r.questionCount}
                        </QuickActionLink>
                      );
                    })}
                  </>
                ) : undefined
              }
            />
            <SplitTileShell
              heroImageSrc="/heroes/grow.png"
              left={
                <>
                  <div className="field-label">
                    {intl.formatMessage({
                      id: "home.strengths",
                      defaultMessage: "Strengths",
                    })}
                  </div>
                  <PowerMiniList rows={strengths} intl={intl} />
                  <div className="mt-auto grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-500 ease-out group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100">
                    <div className="min-h-0 overflow-hidden">
                      <QuickActionLink to="/grow/map" icon="→">
                        {intl.formatMessage({
                          id: "home.viewStrengths",
                          defaultMessage: "Show strengths",
                        })}
                      </QuickActionLink>
                    </div>
                  </div>
                </>
              }
              right={
                <>
                  <div className="field-label">
                    {intl.formatMessage({
                      id: "home.roomToGrow",
                      defaultMessage: "Room to grow",
                    })}
                  </div>
                  <PowerMiniList rows={weaknesses} intl={intl} />
                  <div className="mt-auto grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-500 ease-out group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100">
                    <div className="min-h-0 overflow-hidden">
                      <QuickActionLink to="/grow/map" icon="→">
                        {intl.formatMessage({
                          id: "home.viewRoomToGrow",
                          defaultMessage: "Show room to grow",
                        })}
                      </QuickActionLink>
                    </div>
                  </div>
                </>
              }
            />
            {/* min-h-36, matching StatTile/SplitTileShell's own floor (components/dashboard/StatTile.tsx)
              — this tile doesn't reuse either component (single ranked list, not their label+value+bar
              or two-column shape), so it needs the same value kept in sync by hand. */}
            <div className="box group relative min-h-36 overflow-hidden bg-surface p-4">
              <HeroRevealImage src="/heroes/grow.png" />
              {/* relative: same stacking fix as StatTile/ShareItemCard — an absolutely positioned
                reveal sibling paints above static content regardless of DOM order. */}
              <div className="relative">
                <div className="field-label">
                  {intl.formatMessage({
                    id: "home.inComparison",
                    defaultMessage: "In comparison · Community",
                  })}
                </div>
                {topContacts.length > 0 ? (
                  <ol className="mt-2 space-y-0.5">
                    {topContacts.map((c, i) => (
                      <li key={c.person.id}>
                        <Link
                          to={`/p/${c.person.id}`}
                          title={intl.formatMessage(
                            {
                              id: "home.openProfileOf",
                              defaultMessage: "Open {name}",
                            },
                            {
                              name:
                                c.person.formattedName ??
                                intl.formatMessage({
                                  id: "home.profileFallback",
                                  defaultMessage: "Profile",
                                }),
                            },
                          )}
                          className="flex items-center gap-2 rounded-md px-1 py-1 transition hover:bg-ink/[0.06]"
                        >
                          <span className="w-3 shrink-0 text-[11px] tabular-nums text-ink/40">
                            {i + 1}
                          </span>
                          <Avatar
                            size="sm"
                            person={{
                              name: c.person.formattedName ?? "",
                              avatarContent: c.person.avatar?.content,
                              avatarMime: c.person.avatar?.mimeType,
                            }}
                          />
                          <span className="min-w-0 flex-1 truncate text-[12px]">
                            {c.person.formattedName ?? "–"}
                          </span>
                          <span className="shrink-0 text-[11px] tabular-nums text-ink/55">
                            {c.score}&thinsp;%
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="mt-2 text-[11px] text-ink/55">
                    {intl.formatMessage({
                      id: "home.noCommunity",
                      defaultMessage: "No community yet.",
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Dashboard>
      ) : null}

      <Dashboard
        title="Share" // i18n-allow: SHARE brand name
        to="/share"
        anchor="home.share"
        right={intl.formatMessage({
          id: "home.myPosts",
          defaultMessage: "My posts ↗",
        })}
        graphql="MySharedItems"
      >
        {myLoading ? (
          <div className="text-[11px] text-ink/45">
            {intl.formatMessage({
              id: "common.loading",
              defaultMessage: "Loading…",
            })}
          </div>
        ) : myError ? (
          <div className="text-[11px] text-ink/45">
            {intl.formatMessage(
              {
                id: "common.errorWithMessage",
                defaultMessage: "Error: {message}",
              },
              { message: userErrorMessage(intl, myError) },
            )}
          </div>
        ) : myItems.length === 0 ? (
          <NoBackend
            label={intl.formatMessage({
              id: "home.noOwnPosts",
              defaultMessage: "No posts of your own yet",
            })}
            hint={intl.formatMessage({
              id: "home.noOwnPostsHint",
              defaultMessage: "create a project or a post",
            })}
          />
        ) : (
          <HorizontalCardCarousel>
            {myItems.map((p, i) => (
              <Link
                key={p.id}
                to={`/share/discover/${p.id}`}
                className="block w-64 shrink-0 transition-transform hover:-translate-y-0.5"
              >
                <ShareItemCardCover
                  item={p}
                  index={i}
                  heroImageSrc="/heroes/share.png"
                />
              </Link>
            ))}
          </HorizontalCardCarousel>
        )}
      </Dashboard>

      {hasMatch ? (
        <Dashboard
          title="Match" // i18n-allow: MATCH brand name
          to="/match"
          anchor="home.match"
          right={intl.formatMessage({
            id: "home.allMatches",
            defaultMessage: "All matches ↗",
          })}
          graphql="MatchList"
        >
          {matchLoading ? (
            <div className="text-[11px] text-ink/45">
              {intl.formatMessage({
                id: "common.loading",
                defaultMessage: "Loading…",
              })}
            </div>
          ) : matchError ? (
            <div className="text-[11px] text-ink/45">
              {intl.formatMessage(
                {
                  id: "common.errorWithMessage",
                  defaultMessage: "Error: {message}",
                },
                { message: userErrorMessage(intl, matchError) },
              )}
            </div>
          ) : matches.length === 0 ? (
            <NoBackend
              label={intl.formatMessage({
                id: "home.noMatches",
                defaultMessage: "No matches",
              })}
            />
          ) : (
            // Fixed 4-up grid, same shape as Save/Grow above — recentMatches is already capped to 4,
            // so this never needs to scroll (spark home page feedback: match Save/Grow's card size,
            // drop the scrollbar).
            <div className="grid grid-cols-2 items-start gap-3 @sm:grid-cols-4">
              {recentMatches.map((m) => (
                <HomeMatchCard key={m.id} match={m} intl={intl} />
              ))}
            </div>
          )}
        </Dashboard>
      ) : null}
    </section>
  );
}
